import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Surface, Btn, PageHeader } from "../components/Primitives";
import {
  CreditCard, Check, Warning, ArrowSquareOut, CheckCircle, Sparkle,
} from "@phosphor-icons/react";

const STATUS_LABELS = {
  active:                { label: "Active",            cls: "text-emerald-300" },
  trialing:              { label: "Trialing",          cls: "text-cyan-300" },
  card_required:         { label: "Card required",     cls: "text-amber" },
  past_due:              { label: "Payment past due",  cls: "text-orange-300" },
  unpaid:                { label: "Unpaid",            cls: "text-orange-300" },
  canceled:              { label: "Canceled",          cls: "text-red-300" },
  incomplete:            { label: "Incomplete",        cls: "text-amber" },
  incomplete_expired:    { label: "Expired",           cls: "text-red-300" },
};

const STANDARD_FEATURES = [
  "Aircraft profile & registration tracking",
  "Maintenance & inspection tracking",
  "Renewal reminders (30/7/0 d)",
  "Parts & component life",
  "Calendar / hour / cycle alerts",
  "Aircraft document storage",
  "AD tracking & compliance support",
  "Maintenance forecast",
  "Owner dashboard & email alerts",
];

const PRO_FEATURES = [
  "Everything in Standard",
  "Cost-sharing module",
  "Co-owner cost allocation",
  "Flight cost tracking",
  "Fixed & variable cost tracking",
  "Settlements & reconciliation",
  "Owner usage reports",
  "Payment tracking",
];

function fmtMoney(n, currency = "EUR") {
  return new Intl.NumberFormat("en-IE", { style: "currency", currency, maximumFractionDigits: 0 }).format(n || 0);
}

function fmtTs(ts) {
  if (!ts) return "—";
  const d = typeof ts === "number" ? new Date(ts * 1000) : new Date(ts);
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function daysUntil(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  const ms = d.getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / 86400000));
}

// ─────────────────────────────────────────────────────────────────────────────
// Side-by-side plan card
// ─────────────────────────────────────────────────────────────────────────────
function PlanCard({ plan, cadence, isCurrent, currentCadence, aircraftCount, busy, onStart }) {
  const variant = cadence === "annual" ? plan.annual : plan.monthly;
  const isPro = plan.id === "pro";
  const period = cadence === "annual" ? "year" : "month";
  const total = (variant.unit_amount_cents / 100) * Math.max(1, aircraftCount);
  const ctaLabel = isCurrent
    ? (cadence === currentCadence ? "Current plan" : `Switch to ${cadence === "annual" ? "annual" : "monthly"} billing`)
    : `Start ${plan.name} (${cadence === "annual" ? "annual" : "monthly"})`;
  const disabled = busy || (isCurrent && cadence === currentCadence);

  return (
    <Surface
      className={`p-7 flex flex-col relative ${isPro ? "border-2 border-amber" : "border border-zinc-800"}`}
      data-testid={`plan-card-${plan.id}`}
    >
      {isPro && (
        <div className="absolute -top-3 right-7 px-3 py-1 bg-amber text-black text-[10px] font-mono uppercase tracking-wider">
          Includes cost sharing
        </div>
      )}
      {isCurrent && (
        <div className="absolute -top-3 left-7 px-3 py-1 bg-emerald-500 text-black text-[10px] font-mono uppercase tracking-wider" data-testid={`plan-card-current-${plan.id}`}>
          Current plan
        </div>
      )}
      <div className="mb-5">
        <div className={`text-[10px] uppercase tracking-[0.3em] font-mono mb-1 ${isPro ? "text-amber" : "text-zinc-500"}`}>{plan.id}</div>
        <h3 className="text-2xl font-semibold tracking-tight">{plan.name}</h3>
        <p className="text-xs text-zinc-500 mt-1">{plan.tagline}</p>
      </div>

      <div className="mb-6 pb-6 border-b border-zinc-800">
        <div className="flex items-baseline gap-2">
          <span className="text-4xl font-semibold tracking-tight" data-testid={`price-${plan.id}-${cadence}`}>
            {fmtMoney(variant.list_amount, "EUR")}
          </span>
          <span className="text-sm text-zinc-500">/aircraft/{period}</span>
        </div>
        {cadence === "annual" && (
          <div className="mt-2 text-xs text-zinc-400">
            Equivalent <span className="font-mono">€{(variant.monthly_equivalent_amount ?? 0).toFixed(2)}</span>/aircraft/month
            <span className="ml-2 inline-block px-2 py-0.5 border border-amber/40 bg-amber/10 text-amber font-mono text-[10px] tracking-wider uppercase">
              {variant.discount_pct}% off
            </span>
          </div>
        )}
        {cadence === "annual" && variant.non_refundable && (
          <div className="mt-2 text-[10px] text-zinc-500 font-mono uppercase tracking-wider">
            Annual prepayment is non-refundable
          </div>
        )}
        <div className="mt-3 pt-3 border-t border-zinc-900 text-xs text-zinc-500">
          For your fleet of <span className="text-zinc-300 font-mono">{Math.max(1, aircraftCount)}</span> aircraft →
          <span className="text-zinc-100 font-mono ml-1" data-testid={`total-${plan.id}-${cadence}`}>{fmtMoney(total, "EUR")}</span>/{period}
        </div>
      </div>

      <ul className="space-y-2.5 mb-7 flex-1">
        {(isPro ? PRO_FEATURES : STANDARD_FEATURES).map((f, i) => (
          <li key={i} className="flex items-start gap-2.5 text-sm text-zinc-300">
            <CheckCircle size={15} weight="fill" className="text-amber mt-0.5 shrink-0" />
            <span>{i === 0 && isPro ? <strong className="text-zinc-100">{f}</strong> : f}</span>
          </li>
        ))}
      </ul>

      <Btn
        onClick={() => onStart(plan.id, cadence)}
        disabled={disabled}
        className={`w-full ${isPro ? "" : "bg-zinc-900 hover:bg-zinc-800"}`}
        data-testid={`plan-cta-${plan.id}-${cadence}`}
      >
        <CreditCard size={14} className="inline mr-1.5" />
        {ctaLabel}
      </Btn>
    </Surface>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Trial banner
// ─────────────────────────────────────────────────────────────────────────────
function TrialBanner({ status }) {
  if (status?.subscription_status !== "trialing") return null;
  const days = daysUntil(status?.trial_ends_at);
  const urgent = days !== null && days <= 14;
  return (
    <Surface
      className={`p-5 ${urgent ? "border-amber/60 bg-amber/5" : "border-cyan-500/40 bg-cyan-500/5"}`}
      data-testid="trial-banner"
    >
      <div className="flex items-start gap-3">
        <Sparkle size={20} className={urgent ? "text-amber mt-0.5" : "text-cyan-300 mt-0.5"} />
        <div>
          <div className={urgent ? "text-amber font-medium" : "text-cyan-200 font-medium"}>
            {days === 0 ? "Trial ends today" : `${days} days left in your free trial`}
          </div>
          <div className="text-sm text-zinc-400 mt-1">
            Your {status.plan_label} trial ends on {fmtTs(status.trial_ends_at)}.
            {status.stripe_customer_id
              ? " Your subscription will start automatically — you can cancel any time before then in the billing portal."
              : " Pick a plan below to lock in your subscription. No charges occur until the trial ends."}
          </div>
        </div>
      </div>
    </Surface>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Billing page
// ─────────────────────────────────────────────────────────────────────────────
export default function Billing() {
  const { user } = useAuth();
  // Phase 5 — Any authenticated tenant member can manage billing.
  const [plansResp, setPlansResp] = useState(null);
  const [status, setStatus] = useState(null);
  const [invoices, setInvoices] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [cadence, setCadence] = useState("monthly");
  const [sp] = useSearchParams();
  const sessionFromUrl = sp.get("session_id");
  const cancelled = sp.get("cancelled");
  const upgradeIntent = sp.get("upgrade");

  const load = async () => {
    try {
      const [p, s, inv] = await Promise.all([
        api.get("/billing/plans").then((r) => r.data),
        api.get("/billing/status").then((r) => r.data),
        api.get("/billing/invoices").then((r) => r.data).catch(() => ({invoices: []})),
      ]);
      setPlansResp(p);
      setStatus(s);
      setInvoices(inv?.invoices || []);
      if (s?.billing_cadence) setCadence(s.billing_cadence);
    } catch (e) {
      setError(e?.response?.data?.detail || String(e));
    }
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, []);

  // After Checkout return — poll status briefly so webhook can fire
  useEffect(() => {
    if (!sessionFromUrl) return;
    let polls = 0;
    const handle = setInterval(async () => {
      polls += 1;
      try {
        const s = await api.get("/billing/status").then((r) => r.data);
        setStatus(s);
        if (s?.subscription_status === "active" || s?.subscription_status === "trialing" || polls > 8) {
          clearInterval(handle);
        }
      } catch { clearInterval(handle); }
    }, 2000);
    return () => clearInterval(handle);
  }, [sessionFromUrl]);

  const startCheckout = async (plan, chosenCadence) => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/billing/checkout", {
        origin_url: window.location.origin,
        plan, cadence: chosenCadence,
      });
      window.location.href = data.url;
    } catch (e) {
      setError(e?.response?.data?.detail || String(e));
      setBusy(false);
    }
  };

  const openPortal = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/billing/portal", {
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      setError(e?.response?.data?.detail || String(e));
      setBusy(false);
    }
  };

  // Phase 4F — cancel / resume / restore actions
  const cancelSubscription = async () => {
    if (!window.confirm(
      "Cancel your subscription?\n\n" +
      "You'll keep full access until your current period ends, then the workspace " +
      "becomes read-only. Your data is preserved — you can reactivate later for a " +
      "one-time €100 fee."
    )) return;
    setBusy(true); setError("");
    try {
      await api.post("/billing/cancel", {});
      // refresh
      const s = await api.get("/billing/status").then((r) => r.data);
      setStatus(s);
    } catch (e) {
      setError(e?.response?.data?.detail || String(e));
    } finally {
      setBusy(false);
    }
  };

  const resumeSubscription = async () => {
    setBusy(true); setError("");
    try {
      await api.post("/billing/resume", {});
      const s = await api.get("/billing/status").then((r) => r.data);
      setStatus(s);
    } catch (e) {
      setError(e?.response?.data?.detail || String(e));
    } finally {
      setBusy(false);
    }
  };

  const startDataRestore = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/billing/restore", {
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      setError(e?.response?.data?.detail || String(e));
      setBusy(false);
    }
  };

  const plans = plansResp?.plans || [];
  const statusInfo = STATUS_LABELS[status?.subscription_status] || null;
  const currentPlan = status?.plan;
  const currentCadence = status?.billing_cadence;
  const periodLabel = currentCadence === "annual" ? "Annual total" : "Monthly total";

  return (
    <div className="container py-10 space-y-8" data-testid="billing-page">
      <PageHeader
        overline="Workspace billing"
        title="Subscription & invoicing"
        subtitle="Choose how you pay for your aircraft slots. 90-day free trial. Annual billing saves 20%."
      />

      {/* Post-checkout banner */}
      {sessionFromUrl && (
        <Surface className="p-5 border-emerald-500/40 bg-emerald-500/5" data-testid="post-checkout-banner">
          <div className="flex items-start gap-3">
            <Check size={20} className="text-emerald-400 mt-0.5" />
            <div>
              <div className="text-emerald-200 font-medium">Checkout complete</div>
              <div className="text-sm text-zinc-400 mt-1">Your subscription is being activated. This page will refresh once the webhook confirms.</div>
            </div>
          </div>
        </Surface>
      )}

      {/* Upgrade-intent banner — shown when user arrives from a Pro-only feature */}
      {upgradeIntent === "cost_sharing" && !status?.has_cost_sharing_access && (
        <Surface className="p-5 border-amber/40 bg-amber/5" data-testid="upgrade-intent-banner">
          <div className="flex items-start gap-3">
            <Sparkle size={20} className="text-amber mt-0.5" />
            <div>
              <div className="text-amber font-medium">Upgrade to Pro to unlock cost sharing</div>
              <div className="text-sm text-zinc-400 mt-1">
                Pro adds settlements, reserves, reconciliation, co-owner allocation,
                and flight cost tracking. Pick the Pro plan below to start your
                90-day free trial — no charges occur until the trial ends.
              </div>
            </div>
          </div>
        </Surface>
      )}

      {cancelled && (
        <Surface className="p-5 border-amber/40 bg-amber/5" data-testid="billing-cancelled-banner">
          <div className="flex items-start gap-3">
            <Warning size={20} className="text-amber mt-0.5" />
            <div>
              <div className="text-amber font-medium">Checkout cancelled</div>
              <div className="text-sm text-zinc-300 mt-1">No charge was made. Pick a plan when you're ready.</div>
            </div>
          </div>
        </Surface>
      )}

      {error && (
        <Surface className="p-5 border-red-500/40 bg-red-500/5" data-testid="billing-error">
          <div className="flex items-start gap-3">
            <Warning size={20} className="text-red-400 mt-0.5" />
            <div><div className="text-red-300 font-medium">Billing error</div><div className="text-sm text-zinc-300 mt-1">{error}</div></div>
          </div>
        </Surface>
      )}

      {/* Trial banner */}
      <TrialBanner status={status} />

      {/* Current state */}
      {status && (
        <Surface className="p-6" data-testid="billing-status-card">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono">Current plan</div>
              <div className="text-2xl font-medium mt-1 flex items-center gap-3">
                <span data-testid="current-plan-name">{status.plan_label}</span>
                {status.billing_cadence && (
                  <span className="text-sm font-mono text-zinc-500 uppercase tracking-wider">· {status.billing_cadence}</span>
                )}
                {statusInfo && <span className={`tag font-mono text-[10px] tracking-wider ${statusInfo.cls}`}>{statusInfo.label.toUpperCase()}</span>}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono">{periodLabel}</div>
              <div className="text-2xl font-mono mt-1 text-amber" data-testid="period-total">
                {fmtMoney(status.period_total_amount, "EUR")}
              </div>
              {status.billing_cadence === "annual" && status.period_total_amount > 0 && (
                <div className="text-[10px] text-zinc-500 font-mono mt-1">
                  ≈ {fmtMoney(status.monthly_equivalent_amount, "EUR")}/month
                </div>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-5 pt-5 border-t border-zinc-800">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono">Aircraft</div>
              <div className="text-lg font-mono mt-1" data-testid="aircraft-count">
                {status.aircraft_count}{status.aircraft_cap != null ? ` / ${status.aircraft_cap}` : ""}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono">
                {status.subscription_status === "trialing" ? "Trial ends" : "Renews"}
              </div>
              <div className="text-sm font-mono mt-1">
                {fmtTs(status.subscription_status === "trialing"
                  ? status.trial_ends_at : status.current_period_end)}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono">Billing email</div>
              <div className="text-sm font-mono mt-1 truncate">{status.billing_email || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono">Customer</div>
              <div className="text-sm font-mono mt-1 truncate text-zinc-500">{status.stripe_customer_id || "—"}</div>
            </div>
          </div>

          {/* Phase 4G — card-required banner */}
          {status.subscription_status === "card_required" && (
            <div className="mt-5 p-4 border border-amber/60 bg-amber/5 rounded" data-testid="card-required-banner">
              <div className="text-amber font-medium">Add a payment method to start your 90-day free trial</div>
              <div className="text-sm text-zinc-300 mt-1">
                Your workspace is set up and waiting. The free trial begins the
                moment you add a card — you won't be charged for 90 days, and
                you can cancel any time before then.
              </div>
            </div>
          )}

          {/* Past-due banner */}
          {(status.subscription_status === "past_due" || status.subscription_status === "unpaid") && (
            <div className="mt-5 p-4 border border-orange-500/40 bg-orange-500/5 rounded" data-testid="past-due-banner">
              <div className="text-orange-200 font-medium">Payment failed</div>
              <div className="text-sm text-zinc-300 mt-1">
                Your subscription is past due.
                {status.grace_until ? ` Read-only mode begins ${fmtTs(status.grace_until)}` : " Read-only mode is active"} —
                fix your payment method in the Billing Portal to restore write access.
              </div>
            </div>
          )}

          {/* Phase 4F — cancel-scheduled banner */}
          {status.cancel_at_period_end && status.subscription_status !== "canceled" && (
            <div className="mt-5 p-4 border border-amber/50 bg-amber/5 rounded" data-testid="cancel-scheduled-banner">
              <div className="text-amber font-medium">Subscription scheduled to end</div>
              <div className="text-sm text-zinc-300 mt-1">
                Your workspace will become read-only on{" "}
                <span className="font-mono text-amber">{fmtTs(status.current_period_end)}</span>.
                You keep full access until then. Your data will be preserved — you can
                reactivate for a one-time €{status.data_restore_price_amount || 100} fee.
              </div>
              <Btn variant="secondary" onClick={resumeSubscription} disabled={busy}
                   className="mt-3" data-testid="resume-subscription-btn">
                Resume subscription
              </Btn>
            </div>
          )}

          {/* Phase 4F — cancelled + data preserved banner */}
          {status.subscription_status === "canceled" && status.data_preserved && (
            <div className="mt-5 p-4 border border-red-500/40 bg-red-500/5 rounded" data-testid="cancelled-data-preserved-banner">
              <div className="text-red-300 font-medium">Workspace is paused</div>
              <div className="text-sm text-zinc-300 mt-1">
                Your subscription ended on{" "}
                <span className="font-mono">{fmtTs(status.cancelled_at)}</span>.
                Your aircraft, maintenance history, parts, ADs and documents are
                still preserved. Reactivate any time for a one-time
                €{status.data_restore_price_amount || 100} fee and we'll restore
                everything where you left it.
              </div>
              <Btn onClick={startDataRestore} disabled={busy}
                   className="mt-3" data-testid="restore-data-btn">
                Restore my data · €{status.data_restore_price_amount || 100}
              </Btn>
            </div>
          )}

          <div className="flex flex-wrap gap-3 mt-6">
            {status.stripe_customer_id && (
              <Btn onClick={openPortal} disabled={busy} data-testid="open-portal-btn">
                <ArrowSquareOut size={14} className="inline mr-1.5" />
                Open billing portal
              </Btn>
            )}
            {/* Phase 4F — cancel button: shown when subscription is live and not already scheduled to cancel */}
            {status.stripe_subscription_id
              && !status.cancel_at_period_end
              && ["active", "trialing", "past_due"].includes(status.subscription_status) && (
              <Btn variant="ghost" onClick={cancelSubscription} disabled={busy}
                   data-testid="cancel-subscription-btn"
                   className="text-red-300 hover:text-red-200 border-red-500/30">
                Cancel subscription
              </Btn>
            )}
          </div>
        </Surface>
      )}

      {/* Plan catalogue with monthly/annual toggle */}
      {plans.length > 0 && (
        <div data-testid="plans-grid">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono">Plans</div>
              <div className="text-xl font-medium mt-1">Choose your plan</div>
            </div>
            <div className="inline-flex border border-zinc-800 rounded overflow-hidden text-xs font-mono uppercase tracking-wider" data-testid="cadence-toggle">
              <button
                type="button"
                onClick={() => setCadence("monthly")}
                className={`px-4 py-2 transition-colors ${cadence === "monthly" ? "bg-amber text-black" : "text-zinc-400 hover:text-zinc-200"}`}
                data-testid="cadence-monthly-btn"
              >
                Monthly
              </button>
              <button
                type="button"
                onClick={() => setCadence("annual")}
                className={`px-4 py-2 transition-colors ${cadence === "annual" ? "bg-amber text-black" : "text-zinc-400 hover:text-zinc-200"}`}
                data-testid="cadence-annual-btn"
              >
                Annual <span className="ml-1.5 text-[10px]">·</span> <span className="ml-1 px-1.5 py-0.5 bg-amber/20 text-amber rounded-sm text-[10px]">−20%</span>
              </button>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {plans.map((p) => (
              <PlanCard
                key={p.id} plan={p} cadence={cadence}
                isCurrent={currentPlan === p.id}
                currentCadence={currentCadence}
                aircraftCount={status?.aircraft_count || 1}
                busy={busy}
                onStart={startCheckout}
              />
            ))}
          </div>
          <div className="mt-5 text-center text-xs text-zinc-500 font-mono uppercase tracking-wider">
            90-day free trial · All prices in EUR · VAT shown at checkout where applicable
          </div>
        </div>
      )}

      {/* Phase 5 — Invoices */}
      {invoices && invoices.length > 0 && (
        <Surface className="p-6" data-testid="invoices-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono">Invoices</div>
              <div className="text-xl font-medium mt-1">Recent payments</div>
            </div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono">{invoices.length} total</div>
          </div>
          <div className="border border-zinc-800">
            <table className="w-full text-sm">
              <thead className="border-b border-zinc-800 text-[10px] uppercase tracking-wider text-zinc-500 font-mono">
                <tr>
                  <th className="text-left py-3 px-4">Number</th>
                  <th className="text-left py-3 px-4">Date</th>
                  <th className="text-left py-3 px-4">Status</th>
                  <th className="text-right py-3 px-4">Amount</th>
                  <th className="text-right py-3 px-4"></th>
                </tr>
              </thead>
              <tbody data-testid="invoices-table">
                {invoices.map((inv) => (
                  <tr key={inv.id} className="border-b border-zinc-900 hover:bg-zinc-950/40">
                    <td className="py-2.5 px-4 font-mono text-xs">{inv.number || inv.id?.slice(-12)}</td>
                    <td className="py-2.5 px-4 font-mono text-xs">{fmtTs(inv.created)}</td>
                    <td className="py-2.5 px-4">
                      <span className={`text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 border ${
                        inv.status === "paid" ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/5" :
                        inv.status === "open" ? "border-amber/40 text-amber bg-amber/5" :
                        "border-zinc-800 text-zinc-400"
                      }`}>
                        {inv.status}
                      </span>
                    </td>
                    <td className="py-2.5 px-4 text-right font-mono">
                      {(inv.amount_paid || inv.amount_due) / 100} {inv.currency}
                    </td>
                    <td className="py-2.5 px-4 text-right">
                      {inv.hosted_invoice_url && (
                        <a href={inv.hosted_invoice_url} target="_blank" rel="noopener noreferrer"
                           className="text-xs text-amber hover:underline" data-testid={`invoice-view-${inv.id}`}>View</a>
                      )}
                      {inv.invoice_pdf && (
                        <a href={inv.invoice_pdf} target="_blank" rel="noopener noreferrer"
                           className="text-xs text-amber hover:underline ml-3" data-testid={`invoice-pdf-${inv.id}`}>PDF</a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Surface>
      )}
      {invoices && invoices.length === 0 && status?.stripe_customer_id && (
        <Surface className="p-5 text-center text-xs text-zinc-500 font-mono" data-testid="invoices-empty">
          No invoices yet. Your first invoice will appear after the trial ends.
        </Surface>
      )}
    </div>
  );
}
